import { GetterTree, MutationTree, Commit, ActionTree, Module } from 'vuex';
import { IFetchResult, IFetchRequest } from './../api/store.interface';
import { storeService } from '../api/store.api';
import { Utilities } from '@/features/base/api/utilities';
import findWithAttr, { IVuexState } from '@/store/store';
import { IStore } from '@/features/stores/api/store.interface';
import { ESRCH } from 'constants';

export interface IStoreState {
  stores: IStore[];
  fetchResults: IFetchResult[];
  dirty: boolean;
}

const state: IStoreState = {
  stores: [],
  fetchResults: [],
  dirty: true,
};

const getters: GetterTree<IStoreState, IVuexState> = {
  stores: (state: IStoreState) => state.stores,
  fetchResults: (state: IStoreState) => state.fetchResults,
  dirty: (state: IStoreState) => state.dirty,
};

const mutations: MutationTree<IStoreState> = {
  reset: (state: IStoreState) => {
    state.stores = [];
    state.fetchResults = [];
    state.dirty = true;
  },
  set: (state: IStoreState, stores: IStore[]) => {
    state.stores = stores;
  },
  delete: (state: IStoreState, index: number) => {
    state.stores.splice(index, 1);
  },
  add: (state: IStoreState, store: IStore) => {
    state.stores.push(store);
  },
  update: (state: IStoreState, store: IStore) => {
    const stores = state.stores;
    for (const i in stores) {
      if (stores[i].id === store.id) {
        stores[i] = store;
      }
    }
  },
  setDirty: (state: IStoreState, dirty: boolean) => {
    state.dirty = dirty;
  },
  fetch: (state: IStoreState, result: IFetchResult[]) => {
    state.fetchResults = result;
  },
};

const actions: ActionTree<IStoreState, IVuexState> = {
  reset: ({ commit }: { commit: Commit }) => {
    commit('reset');
  },
  getAll({ commit, state }: { commit: Commit; state: IStoreState }) {
    if (state.dirty) {
      return storeService.all().then((response: IApi<IStore[]>) => {
        commit('set', response.result);
        commit('setDirty', false);
      });
    }
  },
  add({ commit }: { commit: Commit }, store: IStore) {
    return storeService.add(store).then((response: IApi<IStore>) => {
      commit('add', response.result);
    });
  },
  update({ commit }: { commit: Commit }, store: IStore) {
    return storeService.update(store).then((response: IApi<IStore>) => {
      commit('update', response.result);
    });
  },
  fetch({ commit }: { commit: Commit }, stores: IStore[]) {
    return new Promise(resolve => {
      storeService.fetchAll(stores).then((response: IApi<any>[]) => {
        const jobIds = response.map(r => r.result.jobId);
        // TODO: Call jobs status API
        const timer = setInterval(() => {
          storeService.status(jobIds).then((result: any) => {
            let completed: String[] = [];
            jobIds.forEach(id => {
              if (result.result[id].status === 'complete' || result.result[id].status === 'error') completed.push(id);
            });

            if (jobIds.length == completed.length) {
              clearInterval(timer);
              resolve(result);
            }
          });
        }, 1000);
      });
    });
  },
  delete({ commit }: { commit: Commit }, id: number) {
    return storeService.delete(id).then((response: IApi<number>) => {
      const index = Utilities.findWithAttr(state.stores, 'id', id);
      commit('delete', index);
    });
  },
};

const namespaced: boolean = true;

export const store: Module<IStoreState, IVuexState> = { namespaced, state, getters, actions, mutations };
